import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import type { OtpChannel } from '@/types';
import { sellerApi } from '@services/sellerApi';
import { trackEvent } from '@/utils/analytics';
import './SellerRegistration.css';

interface SellerRegistrationForm {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  businessName: string;
  country: string;
  businessDescription: string;
  businessAddress: string;
  taxId: string;
}

function SellerRegistration() {
  const navigate = useNavigate();
  const { register: registerUser, challengeOtpContact, confirmOtpContact, authState } = useAuth();
  const isAuthenticated = authState.isAuthenticated;

  const [formData, setFormData] = useState<SellerRegistrationForm>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: '',
    country: 'IN',
    businessDescription: '',
    businessAddress: '',
    taxId: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpChallengeId, setOtpChallengeId] = useState<string | null>(null);
  const [otpChannel, setOtpChannel] = useState<OtpChannel | null>(null);
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  // Pre-fill email/name if authenticated
  useEffect(() => {
    if (isAuthenticated && authState.user) {
      const user = authState.user;
      const [first, ...last] = (user.name || '').split(' ');
      setFormData(prev => ({
        ...prev,
        email: user.email,
        firstName: user.firstName || first || '',
        lastName: user.lastName || last.join(' ') || '',
      }));
    }
  }, [isAuthenticated, authState.user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation for new users
    if (!isAuthenticated) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      // If NOT authenticated, register first
      if (!isAuthenticated) {
        const fullName = `${formData.firstName} ${formData.lastName}`.trim();
        await registerUser(
          {
            email: formData.email,
            password: formData.password,
            confirmPassword: formData.confirmPassword,
            name: fullName,
            role: 'SELLER'
          },
          'seller'
        );

        const challenge = await challengeOtpContact('EMAIL', { reason: 'seller_registration' });
        setOtpChallengeId(challenge.challengeId);
        setOtpChannel(challenge.channel);
        setOtpCode('');
        setOtpStep(true);
        return; // Wait for OTP
      }

      // If authenticated (skips OTP since user is already verified/logged in)
      await sellerApi.submitApplication({
        businessName: formData.businessName,
        country: formData.country,
        businessDescription: formData.businessDescription || undefined,
        businessAddress: formData.businessAddress || undefined,
        taxId: formData.taxId || undefined,
        contactEmail: formData.email,
        // contactPhone is optional
      });

      trackEvent({
        name: 'seller_registration_submitted',
        category: 'seller_portal',
        properties: {
          businessName: formData.businessName,
          taxIdProvided: Boolean(formData.taxId),
          descriptionLength: formData.businessDescription.length
        }
      });

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtpCode(e.target.value);
    setError(null);
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpChallengeId) {
      setError('Verification challenge expired. Please resend the code.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await confirmOtpContact(otpChallengeId, otpCode);

      // After OTP success, submit application
      await sellerApi.submitApplication({
        businessName: formData.businessName,
        country: formData.country,
        businessDescription: formData.businessDescription || undefined,
        businessAddress: formData.businessAddress || undefined,
        taxId: formData.taxId || undefined,
        contactEmail: formData.email,
      });

      trackEvent({
        name: 'seller_registration_submitted',
        category: 'seller_portal',
        properties: {
          businessName: formData.businessName,
          taxIdProvided: Boolean(formData.taxId),
          descriptionLength: formData.businessDescription.length
        }
      });

      setSuccess(true);
      setOtpStep(false);
      setOtpCode('');
      setOtpChallengeId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError(null);
    setIsResendingOtp(true);

    try {
      const challenge = await challengeOtpContact('EMAIL', { reason: 'seller_registration_resend' });
      setOtpChallengeId(challenge.challengeId);
      setOtpChannel(challenge.channel);
      setOtpCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification code');
    } finally {
      setIsResendingOtp(false);
    }
  };

  if (success) {
    return (
      <div className="seller-registration">
        <div className="registration-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Registration Submitted!</h2>
            <p>
              Your seller account has been created and forwarded to our operations team for review.
              Keep the details below handy—an onboarding specialist will reach out if anything else is needed
              before activation.
            </p>
            <ul className="success-summary">
              <li>
                <span>Business:</span> {formData.businessName}
              </li>
              <li>
                <span>Contact email:</span> {formData.email}
              </li>
              <li>
                <span>Description:</span> {formData.businessDescription || '—'}
              </li>
              <li>
                <span>Country:</span> {formData.country}
              </li>
              <li>
                <span>Address:</span> {formData.businessAddress || '—'}
              </li>
              <li>
                <span>Tax ID:</span> {formData.taxId || '—'}
              </li>
            </ul>
            <div className="success-actions">
              <button
                onClick={() => navigate('/retail')}
                className="back-btn"
              >
                Back to Store
              </button>
              <button
                onClick={() => navigate('/')}
                className="home-btn"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (otpStep) {
    return (
      <div className="seller-registration">
        <div className="registration-container">
          <div className="registration-header">
            <h1>Verify Your Email</h1>
            <p>
              We sent a verification code to {formData.email} via{' '}
              {otpChannel ? otpChannel.toLowerCase() : 'email'}. Enter the code below to finalize your seller
              application.
            </p>
          </div>

          <form onSubmit={handleOtpSubmit} className="registration-form">
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="otpCode">Verification Code *</label>
                <input
                  type="text"
                  id="otpCode"
                  name="otpCode"
                  value={otpCode}
                  onChange={handleOtpChange}
                  required
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={handleResendOtp}
                disabled={isLoading || isResendingOtp}
              >
                {isResendingOtp ? 'Sending...' : 'Resend Code'}
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading || otpCode.trim().length === 0}
              >
                {isLoading ? 'Verifying...' : 'Confirm Verification'}
              </button>
            </div>

            <div className="registration-note">
              <p>
                Need to finish later?{' '}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate('/retail')}
                  disabled={isLoading}
                >
                  Return to Store
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-registration">
      <div className="registration-container">
        <div className="registration-header">
          <h1>Become a Seller</h1>
          <p>Join our marketplace and start selling your products</p>
        </div>

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required={!isAuthenticated}
                  disabled={isLoading || isAuthenticated}
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required={!isAuthenticated}
                  disabled={isLoading || isAuthenticated}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required={!isAuthenticated}
                disabled={isLoading || isAuthenticated}
              />
            </div>

            {!isAuthenticated && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Business Information</h3>
            <div className="form-group">
              <label htmlFor="businessName">Business Name *</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">Seller Country *</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              >
                <option value="IN">India</option>
                <option value="US">United States</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="businessDescription">Business Description *</label>
              <textarea
                id="businessDescription"
                name="businessDescription"
                value={formData.businessDescription}
                onChange={handleInputChange}
                required
                rows={4}
                disabled={isLoading}
                placeholder="Describe your business and the types of products you sell..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="businessAddress">Business Address *</label>
              <textarea
                id="businessAddress"
                name="businessAddress"
                value={formData.businessAddress}
                onChange={handleInputChange}
                required
                rows={3}
                disabled={isLoading}
                placeholder="Full business address including city, state, and postal code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxId">Tax ID / Business Registration Number *</label>
              <input
                type="text"
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                placeholder="e.g., EIN, GST Number, or Business Registration ID"
              />
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/retail')}
              className="cancel-btn"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Register as Seller'}
            </button>
          </div>

          {!isAuthenticated && (
            <div className="registration-note">
              <p>
                <strong>Note:</strong> Your application will be reviewed by our team.
                You'll receive an email notification once approved.
                This process typically takes 1-2 business days.
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default SellerRegistration;
